import * as React from 'react';

import { faSpinnerThird, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import { useMakeGuestInParkingCard } from 'src/features/cards/api/useMakeGuestInParkingCard';
import MakeGuestInParkingCardForm from 'src/features/cards/forms/MakeGuestInParkingCardForm';
import { type InParkingCardDetail } from 'src/features/cards/types/card';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useCreateParkingGuestReason } from 'src/features/parkingManagement/api/ReasonsForGuest/useCreateParkingGuestReason';
import { GUEST_REASONS_RESOURCE } from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages, validationMessagesMap } from 'src/utils/messages';

const makeGuestInParkingCardFormSchema = z
    .object({
        reason: z.string().nullable(),
        reason_other: z.string().optional(),
    })
    .superRefine((values, ctx) => {
        if (values.reason === null && values.reason !== 'reason_other') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: validationMessagesMap.validationErrorRequired,
                path: ['reason'],
            });
        }
        if (values.reason === 'reason_other' && !values.reason_other) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: validationMessagesMap.validationErrorRequired,
                path: ['reason_other'],
            });
        }
    });

type MakeGuestInParkingCardFormValues = z.infer<typeof makeGuestInParkingCardFormSchema>;

interface MakeGuestInParkingCardModalFormProps extends React.HTMLAttributes<HTMLDivElement> {
    inParkingCardDetail?: InParkingCardDetail;
    refetch: () => void;
}

const MakeGuestInParkingCardModalForm = ({
    children,
    inParkingCardDetail,
    refetch,
}: MakeGuestInParkingCardModalFormProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: makeGuestInParkingCard, isPending } = useMakeGuestInParkingCard({
        onSuccess: (card, _) => {
            refetch();
            onOpenChange(false);
            toast({
                title: formatMessage(cardMessages.makeGuestSuccess),
                description: formatMessage(cardMessages.makeGuestSuccessDetails, {
                    client: card.card_number,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: createGuestReason } = useCreateParkingGuestReason({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [GUEST_REASONS_RESOURCE],
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            const errorMessage = error
                ? `${error[0]}, ${error[1][0]}`
                : formatMessage(generalErrorMessages.somethingWentWrongRetry);

            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
            });
        },
    });

    const form = useForm<MakeGuestInParkingCardFormValues>({
        resolver: zodResolver(makeGuestInParkingCardFormSchema),
        defaultValues: {
            reason: undefined,
        },
    });

    const onSubmit = (values: MakeGuestInParkingCardFormValues) => {
        if (!inParkingCardDetail) return null;

        const isOtherReason = values.reason === 'reason_other';

        const submitData = {
            parkingId,
            cardId: inParkingCardDetail?.in_parking_card_id,
            reason: !isOtherReason ? values.reason : null,
            ...(isOtherReason && { reason_other: values.reason_other }),
        };

        makeGuestInParkingCard(submitData);

        if (isOtherReason) {
            createGuestReason({
                parkingId: parkingId,
                name: values.reason_other,
            });
        }
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faUser} />
                        <div>
                            <DialogTitle>{formatMessage(cardMessages.makeGuest)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cardMessages.makeClientGuest, {
                                    client: inParkingCardDetail?.card_number,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <MakeGuestInParkingCardForm form={form} onSubmit={onSubmit} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!form.formState.isValid}
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(cardMessages.makeGuest)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { MakeGuestInParkingCardModalForm, makeGuestInParkingCardFormSchema };
export type { MakeGuestInParkingCardFormValues };
